import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Users, 
  UserPlus, 
  Phone,
  Shield,
  Crown,
  Clock,
  Copy,
  Check,
  Link,
  Share2,
  Trash2,
  UserX,
  UserCheck,
  Camera,
  Upload,
  Image,
  X,
  Key,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  { id: 'security_head', label: 'Security Head' },
  { id: 'security_lead', label: 'Security Lead' },
  { id: 'security_team', label: 'Security Team' },
  { id: 'usher_head', label: 'Usher Head' },
  { id: 'usher_team', label: 'Usher Team' },
  { id: 'nurse', label: 'Nurse' },
  { id: 'first_aid_team', label: 'First Aid Team' },
  { id: 'head_cadet', label: 'Head Cadet' },
  { id: 'cadet', label: 'Cadet' },
  { id: 'sound_team', label: 'Sound Team' },
  { id: 'sunday_school_team', label: 'Sunday School Team' },
  { id: 'nursery_team', label: 'Nursery Team' },
  { id: 'admin', label: 'Admin' },
  { id: 'pastor', label: 'Pastor' },
  { id: 'ministry_team', label: 'Ministry Team' },
];

const TeamPage = () => {
  const { user, isSecurityHead, hasPermission, onlineUsers } = useAuth();
  const [team, setTeam] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteData, setInviteData] = useState({ name: '', email: '', phone: '', role: '' });
  const [createdInvite, setCreatedInvite] = useState(null);
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  
  // Profile picture states
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const fileInputRef = useRef(null);
  
  // Deactivate confirmation states
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [memberToDeactivate, setMemberToDeactivate] = useState(null);

  // Password reset states
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [memberToReset, setMemberToReset] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null); // { name, email, new_password }
  const [copiedPwd, setCopiedPwd] = useState(false);

  // Permanent delete states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Permission checks
  const canInvite = hasPermission('can_invite_members');
  const canDeactivate = hasPermission('can_deactivate_members');
  const canManageTeam = hasPermission('can_manage_team');
  const canResetPasswords = isSecurityHead;

  useEffect(() => {
    fetchTeam();
    if (canInvite) {
      fetchInvites();
    }
  }, [canInvite]);

  const fetchTeam = async () => {
    try {
      const response = await axios.get(`${API_URL}/team`);
      setTeam(response.data);
    } catch (error) {
      toast.error('Failed to fetch team');
    } finally {
      setLoading(false);
    }
  };

  const fetchInvites = async () => {
    try {
      const response = await axios.get(`${API_URL}/invites`);
      setInvites(response.data);
    } catch (error) {
      console.error('Failed to fetch invites:', error);
    }
  };

  // Deactivate team member
  const handleDeactivateClick = (member) => {
    setMemberToDeactivate(member);
    setDeactivateDialogOpen(true);
  };

  const confirmDeactivate = async () => {
    if (!memberToDeactivate) return;
    
    try {
      await axios.put(`${API_URL}/team/${memberToDeactivate.id}/deactivate`);
      toast.success(`${memberToDeactivate.name} has been deactivated`);
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate member');
    } finally {
      setDeactivateDialogOpen(false);
      setMemberToDeactivate(null);
    }
  };

  // Reactivate team member
  const handleReactivate = async (member) => {
    try {
      await axios.put(`${API_URL}/team/${member.id}/reactivate`);
      toast.success(`${member.name} has been reactivated`);
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate member');
    }
  };

  // Reset password handlers
  const handleResetClick = (member) => {
    setMemberToReset(member);
    setResetConfirmOpen(true);
  };

  const confirmResetPassword = async () => {
    if (!memberToReset) return;
    setResetting(true);
    try {
      const response = await axios.post(`${API_URL}/team/${memberToReset.id}/reset-password`);
      setResetResult(response.data);
      setResetConfirmOpen(false);
      setMemberToReset(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  const copyNewPassword = () => {
    if (!resetResult?.new_password) return;
    navigator.clipboard.writeText(resetResult.new_password);
    setCopiedPwd(true);
    setTimeout(() => setCopiedPwd(false), 2000);
    toast.success('Password copied to clipboard');
  };

  const closeResetResult = () => {
    setResetResult(null);
    setCopiedPwd(false);
  };

  // Permanent delete handlers
  const handleDeleteClick = (member) => {
    setMemberToDelete(member);
    setDeleteConfirmText('');
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!memberToDelete) return;
    if (deleteConfirmText.trim() !== memberToDelete.name) {
      toast.error('Name does not match — type it exactly to confirm');
      return;
    }
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/team/${memberToDelete.id}`);
      toast.success(`${memberToDelete.name} permanently deleted`);
      setDeleteDialogOpen(false);
      setMemberToDelete(null);
      setDeleteConfirmText('');
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete member');
    } finally {
      setDeleting(false);
    }
  };

  // Profile picture handlers
  const openProfileDialog = (member) => {
    setSelectedMember(member);
    setPreviewImage(null);
    setProfileDialogOpen(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error('Image too large. Max 5MB allowed.');
      return;
    }
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Use JPEG, PNG, GIF, or WebP.');
      return;
    }
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewImage({ file, dataUrl: e.target.result });
    };
    reader.readAsDataURL(file);
  };

  const uploadProfilePicture = async () => {
    if (!previewImage?.file || !selectedMember) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', previewImage.file);
    
    try {
      const response = await axios.post(
        `${API_URL}/team/${selectedMember.id}/profile-picture`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Profile picture updated!');
      fetchTeam();
      setProfileDialogOpen(false);
      setPreviewImage(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload picture');
    } finally {
      setUploading(false);
    }
  };

  const removeProfilePicture = async () => {
    if (!selectedMember) return;
    
    try {
      await axios.delete(`${API_URL}/team/${selectedMember.id}/profile-picture`);
      toast.success('Profile picture removed');
      fetchTeam();
      setProfileDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove picture');
    }
  };

  const createInvite = async () => {
    if (!inviteData.name || !inviteData.email || !inviteData.role) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/invites`, inviteData);
      setCreatedInvite(response.data);
      toast.success('Invite created!');
      fetchInvites();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invite');
    }
  };

  const copyInviteCode = () => {
    if (createdInvite?.code) {
      navigator.clipboard.writeText(createdInvite.code);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
      toast.success('Code copied to clipboard!');
    }
  };

  const copyInviteLink = () => {
    if (createdInvite?.code) {
      const baseUrl = window.location.origin;
      const inviteLink = `${baseUrl}/join/${createdInvite.code}`;
      navigator.clipboard.writeText(inviteLink);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
      toast.success('Invite link copied!');
    }
  };

  const getInviteLink = (code) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/join/${code}`;
  };

  const copyExistingInviteLink = (code) => {
    const link = getInviteLink(code);
    navigator.clipboard.writeText(link);
    toast.success('Invite link copied!');
  };

  // Delete invite
  const deleteInvite = async (inviteId) => {
    try {
      await axios.delete(`${API_URL}/invites/${inviteId}`);
      toast.success('Invite deleted');
      fetchInvites();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete invite');
    }
  };

  const getRoleBadge = (role) => {
    const roleColors = {
      security_head: 'bg-yellow-600/20 text-yellow-500 border-yellow-600/50',
      security_lead: 'bg-amber-600/20 text-amber-400 border-amber-600/50',
      security_team: 'bg-primary/20 text-primary border-primary/50',
      usher_head: 'bg-orange-600/20 text-orange-400 border-orange-600/50',
      usher_team: 'bg-orange-500/20 text-orange-300 border-orange-500/50',
      nurse: 'bg-red-600/20 text-red-400 border-red-600/50',
      first_aid_team: 'bg-pink-600/20 text-pink-400 border-pink-600/50',
      head_cadet: 'bg-purple-600/20 text-purple-400 border-purple-600/50',
      cadet: 'bg-violet-600/20 text-violet-400 border-violet-600/50',
      sound_team: 'bg-indigo-600/20 text-indigo-400 border-indigo-600/50',
      sunday_school_team: 'bg-cyan-600/20 text-cyan-400 border-cyan-600/50',
      nursery_team: 'bg-rose-600/20 text-rose-400 border-rose-600/50',
      admin: 'bg-slate-600/20 text-slate-400 border-slate-600/50',
      pastor: 'bg-green-600/20 text-green-400 border-green-600/50',
      ministry_team: 'bg-teal-600/20 text-teal-400 border-teal-600/50',
    };
    return roleColors[role] || 'bg-zinc-600/20 text-zinc-400';
  };

  const getStatusBadge = (status) => {
    if (status === 'deactivated') {
      return 'bg-red-600/20 text-red-400 border-red-600/50';
    }
    if (status === 'active') {
      return 'bg-green-600/20 text-green-400 border-green-600/50';
    }
    return 'bg-zinc-600/20 text-zinc-400 border-zinc-600/50';
  };

  const isOnline = (userId) => onlineUsers.includes(userId);

  // Group team by role
  const groupedTeam = team.reduce((acc, member) => {
    const role = member.role;
    if (!acc[role]) acc[role] = [];
    acc[role].push(member);
    return acc;
  }, {});

  const roleOrder = [
    'security_head', 
    'security_lead', 
    'security_team', 
    'usher_head', 
    'usher_team', 
    'nurse', 
    'first_aid_team', 
    'head_cadet',
    'cadet',
    'sound_team', 
    'sunday_school_team', 
    'nursery_team', 
    'admin', 
    'pastor', 
    'ministry_team'
  ];

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="team-page">
      {/* Header with Logo */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <PageHeader 
          title="Team Management" 
          subtitle={`${team.length} members • ${onlineUsers.length} online`}
        />
        
        {canInvite && (
          <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-white text-black hover:bg-gray-200 font-bold uppercase tracking-wide rounded-sm"
                data-testid="invite-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Invite Member
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-panel border-white/10 rounded-sm">
              <DialogHeader>
                <DialogTitle className="font-chivo font-bold text-xl text-white">
                  Invite Team Member
                </DialogTitle>
                <DialogDescription className="font-inter text-muted-foreground">
                  Create an invite code for a new team member
                </DialogDescription>
              </DialogHeader>

              {!createdInvite ? (
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase text-muted-foreground">Name *</Label>
                    <Input
                      placeholder="Full name"
                      value={inviteData.name}
                      onChange={(e) => setInviteData({ ...inviteData, name: e.target.value })}
                      className="bg-black border-white/20 rounded-sm"
                      data-testid="invite-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase text-muted-foreground">Email *</Label>
                    <Input
                      type="email"
                      placeholder="email@truthchurch.com"
                      value={inviteData.email}
                      onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                      className="bg-black border-white/20 rounded-sm"
                      data-testid="invite-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase text-muted-foreground">Phone</Label>
                    <Input
                      type="tel"
                      placeholder="403-XXX-XXXX"
                      value={inviteData.phone}
                      onChange={(e) => setInviteData({ ...inviteData, phone: e.target.value })}
                      className="bg-black border-white/20 rounded-sm"
                      data-testid="invite-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase text-muted-foreground">Role *</Label>
                    <Select 
                      value={inviteData.role} 
                      onValueChange={(v) => setInviteData({ ...inviteData, role: v })}
                    >
                      <SelectTrigger className="bg-black border-white/20 rounded-sm" data-testid="invite-role">
                        <SelectValue placeholder="Select role..." />
                      </SelectTrigger>
                      <SelectContent className="bg-panel border-white/10 rounded-sm">
                        {ROLES.map((role) => (
                          <SelectItem key={role.id} value={role.id} className="font-mono text-sm">
                            {role.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button 
                    onClick={createInvite}
                    className="w-full bg-primary hover:bg-primary/90 font-bold uppercase rounded-sm"
                    data-testid="create-invite-btn"
                  >
                    Create Invite Link
                  </Button>
                </div>
              ) : (
                <div className="space-y-4 pt-4 text-center">
                  <div className="w-16 h-16 rounded-full bg-green-600/20 flex items-center justify-center mx-auto">
                    <Check className="w-8 h-8 text-green-500" />
                  </div>
                  <div>
                    <p className="font-inter text-sm text-muted-foreground mb-2">
                      Invite link for {createdInvite.name}
                    </p>
                    
                    {/* Shareable Link */}
                    <div className="bg-black border border-white/20 rounded-sm p-3 mb-3">
                      <p className="font-mono text-xs text-muted-foreground mb-2">Share this link:</p>
                      <div className="flex items-center gap-2">
                        <code className="font-mono text-xs text-primary flex-1 truncate">
                          {getInviteLink(createdInvite.code)}
                        </code>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={copyInviteLink}
                          className="border-white/20 rounded-sm shrink-0"
                          data-testid="copy-link-btn"
                        >
                          {copiedLink ? <Check className="w-4 h-4 text-green-500" /> : <Link className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>

                    {/* Code Only */}
                    <p className="font-mono text-xs text-muted-foreground mb-1">Or share code only:</p>
                    <div className="flex items-center justify-center gap-2">
                      <code className="font-mono text-2xl tracking-widest text-white bg-black px-4 py-2 rounded-sm">
                        {createdInvite.code}
                      </code>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={copyInviteCode}
                        className="border-white/20 rounded-sm"
                        data-testid="copy-code-btn"
                      >
                        {copiedCode ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setCreatedInvite(null);
                      setInviteData({ name: '', email: '', phone: '', role: '' });
                      setCopiedLink(false);
                    }}
                    className="border-white/20 rounded-sm font-mono text-xs uppercase"
                  >
                    Create Another
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Team Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-panel border border-white/10 rounded-sm p-4 text-center">
          <p className="font-mono text-2xl font-bold text-white">{team.length}</p>
          <p className="font-mono text-xs text-muted-foreground uppercase">Total</p>
        </div>
        <div className="bg-panel border border-green-500/30 rounded-sm p-4 text-center">
          <p className="font-mono text-2xl font-bold text-green-500">{onlineUsers.length}</p>
          <p className="font-mono text-xs text-muted-foreground uppercase">Online</p>
        </div>
        <div className="bg-panel border border-white/10 rounded-sm p-4 text-center">
          <p className="font-mono text-2xl font-bold text-white">{team.filter(m => m.status === 'active').length}</p>
          <p className="font-mono text-xs text-muted-foreground uppercase">Active</p>
        </div>
        <div className="bg-panel border border-red-500/30 rounded-sm p-4 text-center">
          <p className="font-mono text-2xl font-bold text-red-400">{team.filter(m => m.status === 'deactivated').length}</p>
          <p className="font-mono text-xs text-muted-foreground uppercase">Deactivated</p>
        </div>
      </div>

      {/* All Team Members - Compact View */}
      <Card className="bg-panel border-white/10 rounded-sm">
        <CardHeader className="border-b border-white/10 pb-4">
          <CardTitle className="font-chivo font-bold text-lg uppercase tracking-tight text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            All Team Members
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {team.map((member) => (
              <div 
                key={member.id}
                className={`flex items-center gap-4 p-4 bg-surface rounded-sm border ${
                  member.status === 'deactivated' ? 'border-red-600/30 opacity-60' : 'border-white/5'
                }`}
                data-testid={`member-card-${member.id}`}
              >
                {/* Profile Picture */}
                <div className="relative shrink-0">
                  <div 
                    className={`w-12 h-12 rounded-sm bg-black flex items-center justify-center overflow-hidden ${
                      canManageTeam ? 'cursor-pointer hover:ring-2 hover:ring-primary/50' : ''
                    }`}
                    onClick={() => canManageTeam && openProfileDialog(member)}
                  >
                    {member.profile_picture ? (
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}${member.profile_picture}`}
                        alt={member.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="font-chivo font-bold text-base text-white">
                        {member.name.split(' ').map(n => n[0]).join('')}
                      </span>
                    )}
                  </div>
                  {isOnline(member.id) && member.status !== 'deactivated' && (
                    <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-surface" />
                  )}
                </div>

                {/* Member Info: Name, Role badge, Email */}
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p
                      className="font-inter font-semibold text-sm text-white truncate"
                      data-testid={`member-name-${member.id}`}
                    >
                      {member.name}
                    </p>
                    <Badge
                      variant="outline"
                      className={`font-mono text-[10px] uppercase shrink-0 ${getRoleBadge(member.role)}`}
                      data-testid={`member-role-${member.id}`}
                    >
                      {member.role.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                  <p
                    className="font-mono text-xs text-muted-foreground truncate"
                    data-testid={`member-email-${member.id}`}
                    title={member.email}
                  >
                    {member.email}
                  </p>
                  {member.phone && (
                    <p className="font-mono text-[10px] text-muted-foreground/70 truncate flex items-center gap-1">
                      <Phone className="w-2.5 h-2.5" />
                      {member.phone}
                    </p>
                  )}
                </div>

                {/* Quick Status */}
                {member.status === 'deactivated' && (
                  <Badge variant="outline" className="font-mono text-[10px] border-red-600/50 text-red-400 shrink-0">
                    OFF
                  </Badge>
                )}

                {/* Admin Actions */}
                {canDeactivate && member.role !== 'security_head' && member.id !== user.id && (
                  <div className="shrink-0 flex items-center gap-1">
                    {canResetPasswords && member.status === 'active' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleResetClick(member)}
                        className="h-7 w-7 p-0 text-yellow-400 hover:bg-yellow-600/20"
                        title="Reset password"
                        data-testid={`reset-pwd-btn-${member.id}`}
                      >
                        <Key className="w-4 h-4" />
                      </Button>
                    )}
                    {member.status === 'deactivated' ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleReactivate(member)}
                        className="h-7 w-7 p-0 text-green-400 hover:bg-green-600/20"
                        title="Reactivate"
                      >
                        <UserCheck className="w-4 h-4" />
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeactivateClick(member)}
                        className="h-7 w-7 p-0 text-red-400 hover:bg-red-600/20"
                        title="Deactivate (can be reversed)"
                      >
                        <UserX className="w-4 h-4" />
                      </Button>
                    )}
                    {isSecurityHead && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteClick(member)}
                        className="h-7 w-7 p-0 text-red-500 hover:bg-red-700/30"
                        title="Permanently delete (cannot be undone)"
                        data-testid={`delete-member-btn-${member.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Pending Invites */}
      {canInvite && invites.filter(i => !i.used).length > 0 && (
        <Card className="bg-panel border-white/10 rounded-sm">
          <CardHeader className="border-b border-white/10 pb-4">
            <CardTitle className="font-chivo font-bold text-lg uppercase tracking-tight text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-alert-orange" />
              Pending Invites
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="space-y-2">
              {invites.filter(i => !i.used).map((invite) => (
                <div 
                  key={invite.id}
                  className="flex items-center justify-between p-3 bg-surface rounded-sm"
                  data-testid={`invite-${invite.id}`}
                >
                  <div>
                    <p className="font-inter text-sm text-white">{invite.name}</p>
                    <p className="font-mono text-xs text-muted-foreground">{invite.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right mr-2">
                      <code className="font-mono text-sm text-primary">{invite.code}</code>
                      <p className="font-mono text-[10px] text-muted-foreground uppercase mt-1">
                        {invite.role.replace(/_/g, ' ')}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => copyExistingInviteLink(invite.code)}
                      className="border-white/20 rounded-sm h-8 w-8"
                      title="Copy invite link"
                    >
                      <Share2 className="w-3 h-3" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => deleteInvite(invite.id)}
                      className="border-red-600/50 text-red-400 hover:bg-red-600/20 rounded-sm h-8 w-8"
                      title="Delete invite"
                      data-testid={`delete-invite-${invite.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Profile Picture Dialog */}
      <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
        <DialogContent className="bg-panel border-white/10 rounded-sm max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-chivo font-bold text-xl text-white">
              Profile Picture
            </DialogTitle>
            <DialogDescription className="font-inter text-muted-foreground">
              {selectedMember?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            {/* Preview */}
            <div className="flex justify-center">
              <div className="w-32 h-32 rounded-sm bg-black border border-white/20 flex items-center justify-center overflow-hidden">
                {previewImage ? (
                  <img 
                    src={previewImage.dataUrl} 
                    alt="Preview" 
                    className="w-full h-full object-cover"
                  />
                ) : selectedMember?.profile_picture ? (
                  <img 
                    src={`${process.env.REACT_APP_BACKEND_URL}${selectedMember.profile_picture}`}
                    alt={selectedMember?.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="font-chivo font-bold text-4xl text-white/50">
                    {selectedMember?.name?.split(' ').map(n => n[0]).join('')}
                  </span>
                )}
              </div>
            </div>

            {/* File Input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={handleFileSelect}
              className="hidden"
            />

            {/* Actions */}
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => fileInputRef.current?.click()}
                variant="outline"
                className="w-full border-white/20 rounded-sm"
              >
                <Upload className="w-4 h-4 mr-2" />
                Choose Image
              </Button>

              {previewImage && (
                <Button
                  onClick={uploadProfilePicture}
                  disabled={uploading}
                  className="w-full bg-primary hover:bg-primary/90 font-bold uppercase rounded-sm"
                  data-testid="upload-profile-btn"
                >
                  {uploading ? 'Uploading...' : 'Save Picture'}
                </Button>
              )}

              {selectedMember?.profile_picture && !previewImage && (
                <Button
                  onClick={removeProfilePicture}
                  variant="outline"
                  className="w-full border-red-600/50 text-red-400 hover:bg-red-600/20 rounded-sm"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Remove Picture
                </Button>
              )}
            </div>

            <p className="font-mono text-[10px] text-muted-foreground text-center">
              Max 5MB • JPEG, PNG, GIF, WebP
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Deactivate Confirmation Dialog */}
      <AlertDialog open={deactivateDialogOpen} onOpenChange={setDeactivateDialogOpen}>
        <AlertDialogContent className="bg-panel border-white/10 rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-chivo font-bold text-xl text-white">
              Deactivate Team Member?
            </AlertDialogTitle>
            <AlertDialogDescription className="font-inter text-muted-foreground">
              <strong className="text-white">{memberToDeactivate?.name}</strong> will no longer be able to log in to the app. 
              Their data will be preserved and you can reactivate them at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/20 rounded-sm">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeactivate}
              className="bg-red-600 hover:bg-red-700 text-white rounded-sm"
              data-testid="confirm-deactivate-btn"
            >
              <UserX className="w-4 h-4 mr-2" />
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Password Confirmation Dialog */}
      <AlertDialog open={resetConfirmOpen} onOpenChange={setResetConfirmOpen}>
        <AlertDialogContent className="bg-panel border-white/10 rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-chivo font-bold text-xl text-white">
              Reset Password?
            </AlertDialogTitle>
            <AlertDialogDescription className="font-inter text-muted-foreground">
              A new temporary password will be generated for{' '}
              <strong className="text-white">{memberToReset?.name}</strong>. Their current password
              will stop working immediately. The new password will be shown to you{' '}
              <strong className="text-white">once</strong> — share it with them so they can log in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/20 rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmResetPassword}
              disabled={resetting}
              className="bg-yellow-600 hover:bg-yellow-700 text-white rounded-sm"
              data-testid="confirm-reset-pwd-btn"
            >
              <Key className="w-4 h-4 mr-2" />
              {resetting ? 'Resetting...' : 'Reset Password'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Result Dialog - shows the new password ONCE */}
      <Dialog open={!!resetResult} onOpenChange={(open) => { if (!open) closeResetResult(); }}>        <DialogContent className="bg-panel border-yellow-500/40 rounded-sm" data-testid="reset-result-dialog">
          <DialogHeader>
            <DialogTitle className="font-chivo font-bold text-xl text-white flex items-center gap-2">
              <Eye className="w-5 h-5 text-yellow-400" />
              New Password — Visible Once
            </DialogTitle>
            <DialogDescription className="font-inter text-muted-foreground">
              Share this with <strong className="text-white">{resetResult?.name}</strong> (
              <span className="font-mono">{resetResult?.email}</span>) immediately. For security, it
              cannot be retrieved later — only a fresh reset.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="bg-black border border-yellow-500/30 rounded-sm p-4">
              <p className="font-mono text-[10px] text-yellow-400 uppercase mb-2">New Password</p>
              <div className="flex items-center gap-2">
                <code
                  className="font-mono text-xl text-white flex-1 tracking-wider select-all"
                  data-testid="new-password-display"
                >
                  {resetResult?.new_password}
                </code>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={copyNewPassword}
                  className="border-yellow-500/40 rounded-sm shrink-0"
                  data-testid="copy-new-password-btn"
                >
                  {copiedPwd ? (
                    <Check className="w-4 h-4 text-green-400" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
            <div className="bg-yellow-600/10 border border-yellow-500/30 rounded-sm p-3">
              <p className="font-mono text-[10px] text-yellow-400 uppercase mb-1">Reminder</p>
              <p className="font-inter text-xs text-white">
                Tell the member to change this from <strong>Settings → Account</strong> after
                logging in.
              </p>
            </div>
            <Button
              onClick={closeResetResult}
              className="w-full bg-primary hover:bg-primary/90 font-bold uppercase rounded-sm"
              data-testid="close-reset-result-btn"
            >
              I've shared it — Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Permanent Delete Confirmation - type-the-name pattern */}
      <Dialog open={deleteDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setDeleteDialogOpen(false);
          setMemberToDelete(null);
          setDeleteConfirmText('');
        }
      }}>
        <DialogContent className="bg-panel border-red-600/40 rounded-sm" data-testid="delete-member-dialog">
          <DialogHeader>
            <DialogTitle className="font-chivo font-bold text-xl text-red-400 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Permanently Delete Team Member
            </DialogTitle>
            <DialogDescription className="font-inter text-muted-foreground">
              This <strong className="text-red-400">cannot be undone</strong>.{' '}
              <strong className="text-white">{memberToDelete?.name}</strong> will lose access
              immediately, their account will be removed, push subscriptions revoked, and pending
              invites cancelled. Historical alerts and messages they created will be preserved with
              their name as a snapshot.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="bg-red-600/10 border border-red-500/30 rounded-sm p-3 space-y-2">
              <p className="font-mono text-[10px] text-red-400 uppercase">
                If you only want to revoke login (recoverable), use{' '}
                <strong className="text-white">Deactivate</strong> instead.
              </p>
            </div>
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase text-muted-foreground">
                Type{' '}
                <span className="text-white font-bold">{memberToDelete?.name}</span> to confirm
              </Label>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={memberToDelete?.name || ''}
                className="bg-black border-red-600/40 rounded-sm font-mono"
                data-testid="delete-confirm-input"
                autoFocus
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setDeleteDialogOpen(false);
                  setMemberToDelete(null);
                  setDeleteConfirmText('');
                }}
                className="flex-1 border-white/20 rounded-sm"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDelete}
                disabled={
                  deleting ||
                  deleteConfirmText.trim() !== (memberToDelete?.name || '___no_match___')
                }
                className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-sm disabled:opacity-40"
                data-testid="confirm-delete-member-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {deleting ? 'Deleting...' : 'Delete Forever'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamPage;
